<template>
  <JazelAppContainer
    app-name="Coupon Builder"
    @update:account-settings="accountSettings.setAccountSettings($event)"
    @update:session-token="accountSettings.setSessionToken($event)"
    @update:is-client-user="accountSettings.setIsClientUser($event)"
    @update:user-role="accountSettings.setRole($event)"
    @update:product-settings="productSettings.setProductSettings($event)"
  >
    <div class="d-flex align-center">
      <!-- Section tabs -->
      <v-tabs v-model="currentTab" color="primary" class="flex-grow-1">
        <v-tab
          value="placements"
          @click="handleTabClick"
          data-testid="tab-placements"
        >
          <v-icon start class="mr-sm-2">mdi-view-dashboard</v-icon>
          <span class="d-none d-md-block">Placements</span>
        </v-tab>
        <v-tab
          value="coupons"
          @click="handleTabClick"
          data-testid="tab-coupons"
        >
          <v-icon start class="mr-sm-2">mdi-ticket-percent</v-icon>
          <span class="d-none d-md-block">Coupons</span>
        </v-tab>
      </v-tabs>
    </div>

    <v-window v-model="currentTab" :touch="false">
      <!-- Placement dashboard -->
      <v-window-item value="placements">
        <v-card>
          <!-- Placements listing -->
          <Placements
            v-if="selectedPlacementId == null"
            :account-id="accountSettings.accountId"
            :placements="productSettings.placements"
            @update:placementId="selectedPlacementId = $event"
          />
          <!-- Edit Placement -->
          <PlacementSettings
            v-else
            :account-name="accountSettings.accountId"
            :placements="productSettings.placements"
            :placement-id="selectedPlacementId"
            :cdn="cdn"
            @update:placementId="selectedPlacementId = $event"
          />
        </v-card>
      </v-window-item>

      <!-- Coupons dashboard -->
      <v-window-item value="coupons">
        <v-card>
          <!-- Coupons listing -->
          <Coupons
            v-if="selectedCouponId == null && !showAddCoupon"
            @update:couponId="selectedCouponId = $event"
            @show-add-coupon="handleShowAddCoupon"
            :has-admin-access="hasAdminAccess"
          />
          <!-- Edit Coupon -->
          <EditCoupon
            v-else-if="selectedCouponId != null"
            :coupon-id="selectedCouponId"
            :key="selectedCouponId"
            @update:couponId="selectedCouponId = $event"
          />
          <!-- Add Coupon -->
          <AddCoupon
            v-else-if="showAddCoupon"
            @coupon-added="handleCouponAdded"
            @cancel-add="showAddCoupon = false"
          />
        </v-card>
      </v-window-item>
    </v-window>
  </JazelAppContainer>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useCounterStore } from '@/stores/counter'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { JazelAppContainer } from '@jazel/jazel-vuetify'
import Placements from '@/components/Placements.vue'
import PlacementSettings from '@/components/PlacementSettings.vue'
import Coupons from '@/components/Coupons.vue'
import EditCoupon from '@/components/EditCoupon.vue'
import AddCoupon from '@/components/AddCoupon.vue'

const store = useCounterStore()
const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()

const currentTab = ref('placements')
const loading = ref(true)
const cdn: string = import.meta.env.VITE_CDN_URL ?? ''
const selectedPlacementId = ref<string | null>(null)
const selectedCouponId = ref<string | null>(null)
const showAddCoupon = ref(false)

// Admin access computed property
const hasAdminAccess = computed(() => {
  return !!(
    accountSettings.role &&
    (accountSettings.role.toLowerCase().includes('admin') ||
      accountSettings.role.toLowerCase().includes('owner'))
  )
})

onMounted(() => {
  store.fetchAndSetCoupons()
})

const handleTabClick = () => {
  selectedPlacementId.value = null
  selectedCouponId.value = null
  showAddCoupon.value = false
}

const handleShowAddCoupon = () => {
  if (hasAdminAccess.value) {
    showAddCoupon.value = true
  } else {
    // Show error message for non-admin users
    console.warn('Non-admin users cannot add coupons')
  }
}

const handleCouponAdded = (couponId: string) => {
  showAddCoupon.value = false
  selectedCouponId.value = null
}
</script>
